import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {openDesignInNewTab} from '@Libraries/open-design-in-new-tab.library';
import type {PopupBlockedGrowl} from '@Libraries/open-design-in-new-tab.library.types';
import {
  MDE_FEEDBACK_URL_PARAM,
  type MdeJobResult,
  type MdeOutcome,
  MdeStartErrorCode,
  MdeStartKind,
  type MdeStartPayload,
  type MdeStartResponse,
  type MdeStatusRequest,
} from '@Libraries/make-design-editable.library.types';

/** Covers three 540 s attempts plus redeliveries; a shorter wait would give up on runs that still succeed. */
export const MDE_POLL_BUDGET_MS = 1800000;
const MDE_CONVERSION_GROWL_KEY = 'mde-conversion';
const MDE_POPUP_BLOCKED_GROWL_KEY = 'mde-popup-blocked';
const AI_CHAT_IMAGE_SOURCE = 'aiChatImage';
const MDE_FAILED_TEXT_KEY = 'pmwjs_mde_failed';

/** `startFailed` and `enqueueFailed` are server-side failures the user can only retry, so they share the generic failure text. */
const MDE_START_ERROR_TEXT_KEYS: Record<MdeStartErrorCode, string> = {
  [MdeStartErrorCode.FEATURE_DISABLED]: 'pmwjs_mde_error_feature_disabled',
  [MdeStartErrorCode.IMAGE_NOT_FOUND]: 'pmwjs_mde_error_image_not_found',
  [MdeStartErrorCode.IMAGE_NOT_ALLOWED]: 'pmwjs_mde_error_image_not_allowed',
  [MdeStartErrorCode.INSUFFICIENT_CREDITS]: 'pmwjs_insufficient_credits',
  [MdeStartErrorCode.ALREADY_RUNNING]: 'pmwjs_mde_error_already_running',
  [MdeStartErrorCode.START_FAILED]: MDE_FAILED_TEXT_KEY,
  [MdeStartErrorCode.ENQUEUE_FAILED]: MDE_FAILED_TEXT_KEY,
};

/** Rejects on a start refusal; `writeLocal` puts the backend `code` on the thrown Error. */
export const startMdeJob = async (imageHashedFilename: string): Promise<MdeStartResponse> => {
  const response = (await window.PMW.writeLocal('makedesigneditable/start', {imageSource: AI_CHAT_IMAGE_SOURCE, imageHashedFilename})) as MdeStartPayload;
  return toMdeStartResponse(response);
};

const toMdeStartResponse = ({kind, jobId, posterHashedId}: MdeStartPayload): MdeStartResponse => {
  if (kind === MdeStartKind.REUSED && posterHashedId) {
    return {kind, posterHashedId};
  }
  if ((kind === MdeStartKind.STARTED || kind === MdeStartKind.RESUMED) && jobId) {
    return {kind, jobId, posterHashedId: posterHashedId ?? null};
  }
  throw new Error('Make design editable returned an unexpected start response');
};

/** Resolves null on timeout (the job may still finish); rejects on a failed job or a malformed result. */
export const pollMdeJob = async (jobId: string, budgetMs: number): Promise<MdeOutcome | null> => {
  const initiate = (): Promise<MdeStatusRequest> => {
    return Promise.resolve({jobId});
  };

  const result = (await window.PMW.pollingAjaxCallAsync(initiate, checkMdeStatus, budgetMs)) as MdeJobResult | string | undefined;

  if (typeof result === 'string') {
    throw new Error('Make design editable failed');
  }
  if (!result) {
    return null;
  }
  if (!result.posterHashedId || !result.jobHashedId) {
    throw new Error('Make design editable returned an unexpected response');
  }

  return {jobId: result.jobHashedId, posterHashedId: result.posterHashedId};
};

/** The finished design's editor URL with `?mdeFeedback=<jobId>`, so the editor shows its feedback toast. */
export const getMdePosterUrl = ({jobId, posterHashedId}: MdeOutcome): string => {
  return `${getMdeDesignUrl(posterHashedId)}?${MDE_FEEDBACK_URL_PARAM}=${encodeURIComponent(jobId)}`;
};

export const getMdeDesignUrl = (posterHashedId: string): string => {
  return window.PMW.util.site_url(`posterbuilder/load/${encodeURIComponent(posterHashedId)}`);
};

export const openMdeDesignTab = (posterHashedId: string): void => {
  openDesignInNewTab(getMdeDesignUrl(posterHashedId), getMdePopupBlockedGrowl());
};

/** Opens the editor tab that polls the job; falls back to a growl link if the popup is blocked. */
export const openMdePendingEditorTab = (jobId: string, posterHashedId: string): void => {
  const url = window.PMW.util.site_url(`posterbuilder/loadFromMdePending/${encodeURIComponent(posterHashedId)}?jobId=${encodeURIComponent(jobId)}`);
  openDesignInNewTab(url, getMdePopupBlockedGrowl());
};

const getMdePopupBlockedGrowl = (): PopupBlockedGrowl => {
  return {key: MDE_POPUP_BLOCKED_GROWL_KEY, text: window.i18next.t('pmwjs_mde_popup_blocked_open_design')};
};

/** Shared by the in-chat and the editor-tab loading modals. */
export const getMdeLoadingTexts = (): string[] => {
  return [window.i18next.t('pmwjs_mde_preparing_design'), window.i18next.t('pmwjs_mde_layers_slight_changes')];
};

/** Same key as the ongoing growl, so a failure after the handoff replaces the spinner instead of leaving it. */
export const showMdeErrorGrowl = (error: unknown): void => {
  const errorCode = getMdeStartErrorCode(error);
  showMessageGrowl({
    key: MDE_CONVERSION_GROWL_KEY,
    type: GROWL_TYPE.DANGER,
    text: window.i18next.t(errorCode === undefined ? MDE_FAILED_TEXT_KEY : MDE_START_ERROR_TEXT_KEYS[errorCode]),
    showCloseIcon: true,
  });
};

/** Shown when the user dismisses the in-chat loading modal mid-run; the done growl replaces it (same key). */
export const showMdeOngoingGrowl = (): void => {
  showMessageGrowl({
    key: MDE_CONVERSION_GROWL_KEY,
    type: GROWL_TYPE.NEUTRAL,
    text: window.i18next.t('pmwjs_mde_converting'),
    isLoading: true,
    sticky: true,
    showCloseIcon: false,
  });
};

/** Replaces the ongoing growl; the link opens the design in the same tab. */
export const showMdeDoneGrowl = (navigationUrl: string): void => {
  showMessageGrowl({
    key: MDE_CONVERSION_GROWL_KEY,
    type: GROWL_TYPE.NEUTRAL,
    text: window.i18next.t('pmwjs_editable_design_is_ready'),
    icon: 'icon-check-filled',
    iconClassName: 'content-success',
    sticky: true,
    showCloseIcon: false,
    action: {
      label: window.i18next.t('pmwjs_open_in_editor'),
      url: navigationUrl,
      openInNewTab: false,
    },
  });
};

/** Neutral, not a failure: the poll budget ran out but the job may still finish. Same key, so it replaces the ongoing growl. */
export const showMdeStillWorkingGrowl = (): void => {
  showMessageGrowl({
    key: MDE_CONVERSION_GROWL_KEY,
    type: GROWL_TYPE.NEUTRAL,
    text: window.i18next.t('pmwjs_mde_still_working'),
    sticky: true,
    showCloseIcon: true,
  });
};

/** `pollingAjaxCallAsync` reads the `{isAvailable, data}` envelope this answers with. */
const checkMdeStatus = (statusRequest: MdeStatusRequest): Promise<unknown> => {
  return window.PMW.writeLocal('makedesigneditable/status', {jobId: statusRequest.jobId});
};

export const isMdeInsufficientCreditsError = (error: unknown): boolean => {
  return getMdeStartErrorCode(error) === MdeStartErrorCode.INSUFFICIENT_CREDITS;
};

const getMdeStartErrorCode = (error: unknown): MdeStartErrorCode | undefined => {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return undefined;
  }
  const {code} = error;
  return typeof code === 'string' && isMdeStartErrorCode(code) ? code : undefined;
};

const isMdeStartErrorCode = (value: string): value is MdeStartErrorCode => {
  const knownCodes: string[] = Object.values(MdeStartErrorCode);
  return knownCodes.includes(value);
};
